import { useEffect, useMemo } from 'react';

import { useUserWorkspace } from './hooks';
import { useVerticalAppsGlobalRecentActivity } from './recentActivity/useVerticalAppsRecentActivity';
import { useVerticalAppsProjectList } from './useVerticalAppsProjectList';
import { useVerticalAppsSideNavProjectsPreference } from './useVerticalAppsSideNavProjectsPreference';

// Prune the global preference blobs (recent/pinned projects, recent activity) of entries pointing at
// projects that no longer exist. The backend stores these as opaque JSON, so only the FE can clean them.
// Mount on a surface that loads the project list; guards against pruning on a loading/errored list, and
// scopes to the current workspace since the blobs are bucketed per workspace.
export function useReconcileVerticalAppsProjectPreferences() {
  const { projects, isSuccess } = useVerticalAppsProjectList();
  const { currentWorkspaceId } = useUserWorkspace();
  const { pruneSideNavProjects } = useVerticalAppsSideNavProjectsPreference();
  const { pruneRecentActivityForMissingProjects } = useVerticalAppsGlobalRecentActivity();

  const liveProjectIds = useMemo(() => {
    if (!isSuccess || !currentWorkspaceId) return undefined;
    return new Set(
      projects.filter((project) => project.team?._id === currentWorkspaceId).map((project) => project._id)
    );
  }, [projects, isSuccess, currentWorkspaceId]);

  useEffect(() => {
    if (!liveProjectIds) return;
    const isLiveProjectId = (projectId: string) => liveProjectIds.has(projectId);
    pruneSideNavProjects(isLiveProjectId);
    pruneRecentActivityForMissingProjects(isLiveProjectId);
  }, [liveProjectIds, pruneSideNavProjects, pruneRecentActivityForMissingProjects]);
}
